import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { enquiriesApi } from '../modules/enquiries/api/enquiriesApi';
import { pipelineApi as dealsApi } from '../modules/pipeline/api/pipelineApi';

const getCurrentEcode = () => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || '';
  } catch {
    return '';
  }
};

const LocalQuotations = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const [previewRow, setPreviewRow] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [isApproving, setIsApproving] = useState(false);

  const loadRows = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await enquiriesApi.getAllEnquiries({ limit: 500 });
      const sorted = [...data].sort((a, b) => {
        const da = a.EnquiryDate || a.enquiry_date || '';
        const db = b.EnquiryDate || b.enquiry_date || '';
        return db.localeCompare(da);
      });
      setRows(sorted);
    } catch (e) {
      console.error('Failed to load local quotations:', e);
      setError(e.message || 'Failed to load local quotations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const filteredRows = rows.filter((r) => {
    if (filter === 'approved') return !!r.approved_by_name;
    if (filter === 'pending') return !r.approved_by_name;
    return true;
  });

  const approvedCount = rows.filter((r) => r.approved_by_name).length;
  const pendingCount = rows.length - approvedCount;

  const openPreview = async (row) => {
    setPreviewRow(row);
    setPreviewError('');
    setPreviewPdfUrl('');
    if (!row.deal_id) {
      setPreviewError('This enquiry has no linked deal yet, so no offer PDF exists to preview.');
      return;
    }
    setIsLoadingPreview(true);
    try {
      const { blob } = await dealsApi.downloadOfferLetter(row.deal_id);
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (e) {
      console.error('Failed to load quotation preview:', e);
      setPreviewError(e.message || 'Failed to load quotation preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const closePreview = () => {
    if (previewPdfUrl) {
      window.URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewRow(null);
    setPreviewPdfUrl('');
    setPreviewError('');
  };

  const handleApprove = async () => {
    if (!previewRow) return;
    setIsApproving(true);
    try {
      const ecode = getCurrentEcode();
      await enquiriesApi.updateEnquiry(previewRow.id, { approved_by_ecode: ecode });
      await loadRows();
      closePreview();
    } catch (e) {
      console.error('Failed to approve:', e);
      setPreviewError(e.message || 'Failed to approve');
    } finally {
      setIsApproving(false);
    }
  };

  const handleModify = () => {
    if (!previewRow) return;
    navigate(`/enquiries?enquiryId=${previewRow.id}&edit=1`);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Local Quotations</h1>
        <p className="text-sm text-gray-500 mt-1">
          Every local offer, across all sales coordinators - click a row to view the quotation and approve.
        </p>
      </div>

      <div className="flex gap-3 mb-4">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 text-sm rounded-md border ${filter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          All ({rows.length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-3 py-1.5 text-sm rounded-md border ${filter === 'pending' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          Pending Approval ({pendingCount})
        </button>
        <button
          onClick={() => setFilter('approved')}
          className={`px-3 py-1.5 text-sm rounded-md border ${filter === 'approved' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
        >
          Approved ({approvedCount})
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Customer</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">SC</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Offer No</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Approved By</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => openPreview(r)}
                >
                  <td className="px-4 py-3 text-gray-900">{r.AccountName || '-'}</td>
                  <td className="px-4 py-3 text-gray-700">{r.OwnerName || r.OwnerECode || '-'}</td>
                  <td className="px-4 py-3 text-gray-700">{r.offer_no || '-'}</td>
                  <td className="px-4 py-3 text-gray-700">{r.DerivedStatus || r.Status || '-'}</td>
                  <td className="px-4 py-3">
                    {r.approved_by_name ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                        {r.approved_by_name}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{r.EnquiryDate || '-'}</td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    No quotations found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {previewRow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{previewRow.AccountName}</h2>
                <p className="text-sm text-gray-500">{previewRow.offer_no}</p>
              </div>
              <button onClick={closePreview} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 bg-gray-100">
              {isLoadingPreview && <p className="text-sm text-gray-500 text-center py-12">Loading quotation...</p>}
              {previewError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-600">{previewError}</p>
                </div>
              )}
              {previewPdfUrl && !isLoadingPreview && (
                <iframe title="Quotation Preview" src={previewPdfUrl} className="w-full h-[65vh] rounded border border-gray-300 bg-white" />
              )}
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex gap-3">
              <button
                onClick={closePreview}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={handleModify}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Modify
              </button>
              <button
                onClick={handleApprove}
                disabled={isApproving || !!previewRow.approved_by_name}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {previewRow.approved_by_name
                  ? `Already approved by ${previewRow.approved_by_name}`
                  : isApproving
                    ? 'Approving...'
                    : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalQuotations;
